import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { CartItemResponse, MenuItemResponse } from "@/api/lib/db/types";
import {
	CATEGORY_EXTRAS_MAP,
	getNormalizedSizeName,
} from "@/src/lib/constants";
import Icon from "../../../lib/components/custom/Icon";
import { Button } from "../../../lib/components/ui/button";
import { Checkbox } from "../../../lib/components/ui/checkbox";
import { Dialog, DialogContent } from "../../../lib/components/ui/dialog";
import { Label } from "../../../lib/components/ui/label";
import { ScrollArea } from "../../../lib/components/ui/scroll-area";
import { useApi } from "../../../lib/hooks/api/use-api";
import { useSplashStore } from "../../../lib/hooks/use-store";

interface ExtrasDialogProps {
	item: MenuItemResponse;
	selectedSize: string;
	selectedSizeName: string;
	quantity: number;
	basePrice: number;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onItemAddedToCart?: () => void;
	cartItem?: CartItemResponse;
}

export default function ExtrasDialog({
	item,
	selectedSize,
	selectedSizeName,
	quantity,
	basePrice,
	open,
	onOpenChange,
	onItemAddedToCart,
	cartItem,
}: ExtrasDialogProps) {
	const { t } = useTranslation();
	const [selectedExtras, setSelectedExtras] = useState<Record<number, number>>(
		{},
	);
	const [isAdding, setIsAdding] = useState(false);

	const { cart, auth, menu } = useApi();
	const { data: session } = auth.useSession();
	const { data: extrasData } = menu.getExtras({ limit: 1000 });
	const splash = useSplashStore();

	React.useEffect(() => {
		if (open && cartItem) {
			const extras: Record<number, number> = {};
			if (cartItem.extras) {
				for (const e of cartItem.extras) {
					if (e.menuExtraId) {
						extras[e.menuExtraId] = e.quantity;
					}
				}
			}
			setSelectedExtras(extras);
		} else if (open && !cartItem) {
			setSelectedExtras({});
		}
	}, [open, cartItem]);

	const applicableExtras = useMemo(() => {
		if (!item || !extrasData?.extras) return [];

		const allowedCategories = CATEGORY_EXTRAS_MAP[item.category] || [];

		return extrasData.extras.filter((extra) => {
			if (!allowedCategories.includes(extra.category as string)) return false;

			const metadata = extra.metadata
				? JSON.parse(extra.metadata as string)
				: {};

			if (
				selectedSizeName &&
				metadata?.availableSizes &&
				metadata.availableSizes.length > 0
			) {
				const normalizedSize = getNormalizedSizeName(selectedSizeName);
				if (!metadata.availableSizes.includes(normalizedSize)) return false;
			}

			return true;
		});
	}, [item, extrasData, selectedSizeName]);

	const extrasPrice = useMemo(() => {
		let total = 0;
		const normalizedSizeName = getNormalizedSizeName(selectedSizeName);

		Object.entries(selectedExtras).forEach(([extraId, qty]) => {
			const extraIdNum = parseInt(extraId, 10);
			const extra = applicableExtras.find((e) => e.id === extraIdNum);
			if (!extra) return;

			const pricing = extra.pricingRules
				? JSON.parse(extra.pricingRules as string)
				: {};
			let unitPrice = 0;

			if (pricing?.fixedPrice) {
				unitPrice = pricing.fixedPrice;
			} else if (pricing?.sizeBased?.[normalizedSizeName]) {
				unitPrice = pricing.sizeBased[normalizedSizeName];
			} else if (pricing?.firstFreeExtraPrice) {
				unitPrice = pricing.firstFreeExtraPrice;
			}

			total += unitPrice * qty;
		});

		return total;
	}, [selectedExtras, applicableExtras, selectedSizeName]);

	const handleToggleExtra = (extraId: number, checked: boolean) => {
		setSelectedExtras((prev) => {
			const next = { ...prev };
			if (checked) {
				next[extraId] = 1;
			} else {
				delete next[extraId];
			}
			return next;
		});
	};

	const handleAction = async () => {
		if (!session?.user?.id) {
			toast.error(t("home.item_dialog.sign_in_required"));
			onOpenChange(false);
			return;
		}

		try {
			setIsAdding(true);

			const extrasPayload = Object.entries(selectedExtras).map(([id, qty]) => ({
				menuExtraId: parseInt(id, 10),
				quantity: qty,
			}));

			if (cartItem) {
				await cart.updateItem.mutateAsync({
					cartItemId: cartItem.id,
					quantity: {
						quantity,
						menuSizeId: parseInt(selectedSize, 10),
						extras: extrasPayload,
					},
				});
				toast.success(t("home.item_dialog.cart_updated"));
			} else {
				await cart.addItem.mutateAsync({
					userId: session.user.id,
					item: {
						menuItemId: item.id,
						menuSizeId: parseInt(selectedSize),
						quantity,
						extras: extrasPayload,
					},
				});

				splash.show({
					title: t("home.item_dialog.delicious_choice"),
					description: t("home.item_dialog.added_to_cart", { name: item.name }),
					variant: "celebration",
					duration: 3000,
				});
			}

			onItemAddedToCart?.();
			onOpenChange(false);
		} catch {
			toast.error(
				cartItem
					? t("home.item_dialog.failed_to_update")
					: t("home.item_dialog.failed_to_add"),
			);
		} finally {
			setIsAdding(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[600px] p-0 h-[90vh] max-h-[800px] overflow-hidden rounded-2xl border-0 bg-card flex flex-col">
				<div className="p-6 border-b shrink-0">
					<div className="flex items-center gap-3">
						<Button
							variant="ghost"
							size="sm"
							onClick={() => onOpenChange(false)}
							className="p-0 h-8 w-8"
						>
							<Icon name="ArrowLeft" className="w-5 h-5" />
						</Button>
						<div>
							<h2 className="text-xl font-semibold">
								{t("home.extras_dialog.title", { name: item.name })}
							</h2>
						</div>
					</div>
				</div>

				<div className="flex-1 min-h-0">
					<ScrollArea className="h-full">
						<div className="p-6">
							{applicableExtras.length === 0 ? (
								<div className="text-center py-8 text-muted-foreground">
									{t("home.extras_dialog.no_extras")}
								</div>
							) : (
								<div className="space-y-6">
									{Array.from(new Set(applicableExtras.map((e) => e.type))).map(
										(type) => {
											const typeExtras = applicableExtras.filter(
												(e) => e.type === type,
											);
											return (
												<div key={type} className="space-y-3">
													<h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
														{type}
													</h4>
													<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
														{typeExtras.map((extra) => {
															const pricing = extra.pricingRules
																? JSON.parse(extra.pricingRules as string)
																: {};
															const normalizedSizeName =
																getNormalizedSizeName(selectedSizeName);
															let price = 0;
															if (pricing?.fixedPrice)
																price = pricing.fixedPrice;
															else if (pricing?.sizeBased?.[normalizedSizeName])
																price = pricing.sizeBased[normalizedSizeName];

															return (
																<div
																	key={extra.id}
																	className="flex items-center space-x-3 bg-muted/30 rounded-lg p-2"
																>
																	<Checkbox
																		id={`extra-${extra.id}`}
																		checked={!!selectedExtras[extra.id]}
																		onCheckedChange={(checked) =>
																			handleToggleExtra(
																				extra.id,
																				checked as boolean,
																			)
																		}
																	/>
																	<Label
																		htmlFor={`extra-${extra.id}`}
																		className="flex-1 flex justify-between items-center cursor-pointer"
																	>
																		<span className="font-medium">
																			{extra.name}
																		</span>
																		<span className="text-muted-foreground text-sm">
																			{price > 0
																				? `+${price.toFixed(2)}€`
																				: t("common.free")}
																		</span>
																	</Label>
																</div>
															);
														})}
													</div>
												</div>
											);
										},
									)}
								</div>
							)}
						</div>
					</ScrollArea>
				</div>

				<div className="p-6 border-t bg-card">
					<div className="space-y-2 mb-4">
						<div className="flex items-center justify-between">
							<span className="text-sm text-muted-foreground">
								{t("home.extras_dialog.base_price")}
							</span>
							<span className="text-sm font-medium">
								{(basePrice * quantity).toFixed(2)}€
							</span>
						</div>
						{extrasPrice > 0 && (
							<div className="flex items-center justify-between">
								<span className="text-sm text-muted-foreground">
									{t("home.extras_dialog.extras")}
								</span>
								<span className="text-sm font-medium">
									+{(extrasPrice * quantity).toFixed(2)}€
								</span>
							</div>
						)}
						<div className="flex items-center justify-between pt-2 border-t">
							<span className="text-lg font-medium">{t("common.total")}</span>
							<span className="text-2xl font-bold text-primary">
								{((basePrice + extrasPrice) * quantity).toFixed(2)}€
							</span>
						</div>
					</div>

					<Button
						onClick={handleAction}
						disabled={isAdding}
						className="w-full h-12 text-lg font-medium rounded-xl"
						variant="primary"
					>
						{isAdding ? (
							<Icon name="Loader" className="w-4 h-4 animate-spin" />
						) : cartItem ? (
							t("home.item_dialog.update_cart")
						) : (
							t("home.item_dialog.add_to_cart")
						)}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}
